import { setCustomElementMetadata } from "@/lib/customElements";
import { Event } from "@/models/event";
import { User } from "@/models/user";
import { EventService } from "@/services/event";
import { UsersService } from "@/services/user";
import {
  DatesSetArg,
  EventChangeArg,
  EventClickArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../base/button";
import { Combobox } from "../base/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../base/tooltip";

interface CalendarViewProps {
  apiUrl: string;
  apiStage: string;
}

const ONLY_OWN_EVENTS_ROLES = [
  "Accounting",
  "Sales",
  "No System Role",
  "Canvasser",
  "Marketing",
  "Insurance",
  "Crew Member",
];

// const CAN_SEE_ALL_PERSONAL_EVENTS_ROLES = [
//   "Project Manager",
// ];

// Color mapping for different event types
const EVENT_TYPE_COLORS: Record<string, string> = {
  Inspection: "#ef4444", // red
  "Service Call": "#06b6d4", // cyan
  "Client Appointment": "#1a04b3", // blue
  Other: "#10b981", // green
  "Personal Time": "#8b5cf6", // violet
  Adjustment: "#f59e0b", // amber
  Default: "#6b7280", // gray
} as const;

const EVENT_TYPE_TITLES: Record<string, string> = {
  Inspection: "Inspection",
  "Service Call": "Service",
  "Client Appointment": "Client Appt",
  Other: "Other",
  "Personal Time": "Personal",
  Adjustment: "Adjustment",
  Default: "Default",
};

export function CalendarView({ apiUrl, apiStage }: CalendarViewProps) {
  const [events, setEvents] = useState<EventInput[]>([]);
  const [allEvents, setAllEvents] = useState<EventInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<string>(() => {
    return localStorage.getItem("calendar-view") || "dayGridMonth";
  });
  const [currentDate, setCurrentDate] = useState<string>(() => {
    return (
      localStorage.getItem("calendar-date") ||
      new Date().toISOString().split("T")[0]
    );
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [selectedEventType, setSelectedEventType] = useState<string>("all");
  const [users, setUsers] = useState<User[]>([]);
  const calendarRef = useRef<FullCalendar>(null);
  const eventServiceRef = useRef<EventService | null>(null);
  const usersServiceRef = useRef<UsersService | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      if (data.type === "refreshCalendar") {
        loadEvents();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Initialize services
  useEffect(() => {
    if (apiUrl && apiStage) {
      eventServiceRef.current = new EventService(apiUrl, apiStage);
      usersServiceRef.current = new UsersService(apiUrl, apiStage);
      loadEvents();
      loadUsers();
    }
  }, [apiUrl, apiStage]);

  const loadUsers = async () => {
    if (!usersServiceRef.current) return;

    try {
      const usersData = await usersServiceRef.current.getAllUsers();
      setUsers(usersData.filter((u) => u.ustatus !== "Disabled"));
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  const loadEvents = useCallback(
    async (type?: string, start?: string) => {
      if (!eventServiceRef.current) return;

      try {
        setLoading(true);
        setError(null);
        setEvents([]);

        // Calculate date range based on current view and date
        const baseDate = new Date(
          new Date(start || currentDate).getTime() +
            new Date(start || currentDate).getTimezoneOffset() * 60000
        );
        let startDate: string;
        let endDate: string;

        switch (type || currentView) {
          case "dayGridMonth": {
            // For month view, get the full month
            const monthStart = new Date(
              baseDate.getFullYear(),
              baseDate.getMonth(),
              1
            );
            const monthEnd = new Date(
              baseDate.getFullYear(),
              baseDate.getMonth() + 1,
              0
            );
            startDate = monthStart.toISOString().split("T")[0];
            endDate = monthEnd.toISOString().split("T")[0];
            break;
          }
          case "timeGridWeek":
          case "listWeek": {
            // For week views, get the full week
            const weekStart = new Date(baseDate);
            weekStart.setDate(baseDate.getDate() - baseDate.getDay()); // Start of week (Sunday)
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)
            startDate = weekStart.toISOString().split("T")[0];
            endDate = weekEnd.toISOString().split("T")[0];
            break;
          }
          case "timeGridDay": {
            // For day view, just use the current date
            const dayStart = new Date(baseDate);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(dayStart);
            dayEnd.setHours(23, 59, 59, 999);
            startDate = dayStart.toISOString().split("T")[0];
            endDate = dayEnd.toISOString().split("T")[0];
            break;
          }
          default: {
            // Default to month view range
            const defaultMonthStart = new Date(
              baseDate.getFullYear(),
              baseDate.getMonth(),
              1
            );
            const defaultMonthEnd = new Date(
              baseDate.getFullYear(),
              baseDate.getMonth() + 1,
              0
            );
            startDate = defaultMonthStart.toISOString().split("T")[0];
            endDate = defaultMonthEnd.toISOString().split("T")[0];
          }
        }

        const eventsData = await eventServiceRef.current.getActiveEvents(
          startDate,
          endDate
        );
        const user = JSON.parse(localStorage.getItem("raesUser") || "{}");
        const formattedEvents = eventsData
          .filter((event) => {
            if (
              ONLY_OWN_EVENTS_ROLES.some((role) =>
                user?.uroles?.split(":").includes(role)
              )
            ) {
              if (event.etype === "Personal Time" 
                // &&
                // CAN_SEE_ALL_PERSONAL_EVENTS_ROLES.some((role) =>
                //   user?.uroles?.split(":").includes(role)
                // )
              ) {
                return true;
              }
              if (event.etype === "Other" &&
                event.eauids?.split(":").some((uid: string) =>
                  uid === user.uid.toString()
                )
              ) {
                return true;
              }
              return (
                event.euid?.toString() === user.uid.toString() ||
                event.eauid?.toString() === user.uid.toString()
              );
            }
            return true;
          })
          .map(formatEventForCalendar);
        setAllEvents(formattedEvents);
        setEvents(formattedEvents);
      } catch (err) {
        console.error("Failed to load events:", err);
        setError("Failed to load events. Please try again.");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [currentView, currentDate, eventServiceRef]
  );

  const formatEventForCalendar = (event: Event): EventInput => {
    const eventType = event.etype || "Default";
    const color = EVENT_TYPE_COLORS[eventType] || EVENT_TYPE_COLORS.Default;
    const title =
      EVENT_TYPE_TITLES[eventType]?.toUpperCase() || event.etype?.toUpperCase();

    // Format dates to ensure they're treated as local timezone
    const formatDateForLocal = (date: Date | null): string | undefined => {
      if (!date) return undefined;

      // Create a new date in local timezone by using the date components
      const localDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
      );

      // Return ISO string without timezone offset to ensure local interpretation
      return localDate.toISOString().slice(0, -1); // Remove 'Z' suffix
    };

    const building = event.building;

    const person =
      event.etype === "Personal Time" ? event.creator : event.staff;

    return {
      id: event.eid.toString(),
      title: event.etitle || title || "Untitled Event",
      subtitle: event.etype?.toUpperCase(),
      start: formatDateForLocal(event.estartdts),
      end: formatDateForLocal(event.eenddts),
      allDay: event.eallday === "Yes",
      backgroundColor: color,
      borderColor: color,
      textColor: "#ffffff",
      extendedProps: {
        description: person ? person.ufirstn + " " + person.ulastn : null,
        location: building
          ? `${building.baddr1 ?? ""} ${building.baddr2 ?? ""} ${
              building.bcity ?? ""
            } ${building.bst ?? ""} ${building.bzip ?? ""}`.toUpperCase()
          : null,
        type: event.etype,
        status: event.estatus,
        duration: event.eduration,
        workType: event.eworktype,
        services: event.eservices,
        originalEvent: event,
        notes: event.edesc,
      },
    };
  };

  const eventContent = (arg: EventContentArg) => {
    const time = arg.event.start?.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const eventDiv = (
      <div className="fc-event-main">
        <div className="fc-event-main-frame">
          <div className="fc-event-time" title={time}>
            {time}
          </div>
          <div className="fc-event-title-container">
            <div className="fc-event-title fc-sticky">
              <div>{arg.event.title}</div>
              {arg.event.extendedProps.location && (
                <>
                  <div style={{ fontSize: "10.5px" }}>
                    {arg.event.extendedProps.location}
                  </div>
                </>
              )}
              {arg.event.extendedProps.description && (
                <div>{arg.event.extendedProps.description}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );

    if (arg.event.extendedProps.notes) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{eventDiv}</TooltipTrigger>
            <TooltipContent>
              <p>{arg.event.extendedProps.notes}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return eventDiv;
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const { event } = clickInfo;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).$("#modal-container").modal("show");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).goM("raes-eventsAddEdit", "edit1", event.id, null);
  };

  const handleEventChange = async (changeInfo: EventChangeArg) => {
    const { event } = changeInfo;
    const originalEvent = event.extendedProps.originalEvent as Event;

    if (!eventServiceRef.current) {
      return;
    }

    try {
      // Convert FullCalendar dates to local timezone by adjusting for timezone offset
      const convertToLocalDate = (date: Date | null): Date | null => {
        if (!date) return null;

        // FullCalendar might be providing dates with timezone offset applied
        // We need to adjust back to treat as local time
        const offsetMinutes = date.getTimezoneOffset();
        const adjustedDate = new Date(
          date.getTime() - offsetMinutes * 60 * 1000
        );

        return adjustedDate;
      };

      const newStartDate = convertToLocalDate(event.start);
      const newEndDate = convertToLocalDate(event.end);

      // Calculate the time difference to adjust end date accordingly if we only have a start date
      let adjustedEndDate = newEndDate;
      if (
        newStartDate &&
        !newEndDate &&
        originalEvent.estartdts &&
        originalEvent.eenddts
      ) {
        const originalDuration =
          originalEvent.eenddts.getTime() - originalEvent.estartdts.getTime();
        adjustedEndDate = new Date(newStartDate.getTime() + originalDuration);
      }

      // Prepare updated event data
      const updatedEvent = {
        ...originalEvent,
        estartdts: newStartDate,
        eenddts: adjustedEndDate,
      };

      // Update the event via the service
      await eventServiceRef.current.updateEvent(updatedEvent);
    } catch (err) {
      console.error("Failed to update event:", err);
      // Refresh events to revert changes
      await loadEvents();
      alert("Failed to update event. Please try again.");
    }
  };

  const handleDatesSet = useCallback(
    (dateInfo: DatesSetArg) => {
      // Save the current view and date to localStorage
      const type = dateInfo.view.type;
      const start = dateInfo.view.currentStart.toISOString().split("T")[0];

      if (type === currentView && start === currentDate) {
        return;
      }

      setCurrentView(type);
      setCurrentDate(start);

      localStorage.setItem("calendar-view", dateInfo.view.type);
      localStorage.setItem(
        "calendar-date",
        dateInfo.view.currentStart.toISOString().split("T")[0]
      );

      loadEvents(type, start);
    },
    [currentView, currentDate, loadEvents]
  );

  // Filter events based on selected filters
  useEffect(() => {
    let filteredEvents = [...allEvents];

    if (selectedUser && selectedUser !== "all") {
      filteredEvents = filteredEvents.filter(
        (event) =>
          event.extendedProps?.originalEvent?.euid?.toString() === selectedUser
      );
    }

    if (selectedEventType && selectedEventType !== "all") {
      filteredEvents = filteredEvents.filter(
        (event) => event.extendedProps?.type === selectedEventType
      );
    }

    setEvents(filteredEvents);
  }, [allEvents, selectedUser, selectedEventType]);

  if (loading && initialLoad) {
    return (
      <div className="h-full calendar-view">
        {/* Skeleton for Calendar Header */}
        <div className="mb-4 animate-pulse">
          <div className="flex justify-between items-center p-4 bg-gray-50 rounded-t-lg border">
            <div className="flex gap-2">
              <div className="w-12 h-8 bg-gray-300 rounded"></div>
              <div className="w-12 h-8 bg-gray-300 rounded"></div>
              <div className="w-16 h-8 bg-gray-300 rounded"></div>
            </div>
            <div className="w-32 h-8 bg-gray-300 rounded"></div>
            <div className="flex gap-2">
              <div className="w-16 h-8 bg-gray-300 rounded"></div>
              <div className="w-20 h-8 bg-gray-300 rounded"></div>
              <div className="w-16 h-8 bg-gray-300 rounded"></div>
              <div className="w-20 h-8 bg-gray-300 rounded"></div>
              <div className="w-12 h-8 bg-gray-300 rounded"></div>
              <div className="w-20 h-8 bg-gray-300 rounded"></div>
            </div>
          </div>
        </div>

        {/* Skeleton for Calendar Grid */}
        <div className="rounded-b-lg border animate-pulse">
          {/* Days of week header */}
          <div className="grid grid-cols-7 bg-gray-50 border-b">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="p-3 text-center border-r last:border-r-0"
              >
                <div className="mx-auto w-8 h-4 bg-gray-300 rounded"></div>
              </div>
            ))}
          </div>

          {/* Calendar grid rows */}
          {Array.from({ length: 5 }).map((_, weekIndex) => (
            <div
              key={weekIndex}
              className="grid grid-cols-7 border-b last:border-b-0"
            >
              {Array.from({ length: 7 }).map((_, dayIndex) => (
                <div
                  key={dayIndex}
                  className="p-2 h-32 bg-white border-r last:border-r-0"
                >
                  {/* Day number skeleton */}
                  <div className="mb-2 w-6 h-4 bg-gray-200 rounded"></div>

                  {/* Event skeletons - randomly show 0-3 events per day */}
                  {Array.from({ length: Math.floor(Math.random() * 4) }).map(
                    (_, eventIndex) => (
                      <div key={eventIndex} className="mb-1">
                        <div
                          className="h-5 text-xs rounded"
                          style={{
                            backgroundColor:
                              Object.values(EVENT_TYPE_COLORS)[
                                Math.floor(
                                  Math.random() *
                                    Object.values(EVENT_TYPE_COLORS).length
                                )
                              ],
                            opacity: 0.3,
                          }}
                        ></div>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Skeleton for Event Type Legend */}
        <div className="p-4 mt-4 bg-gray-50 rounded-lg animate-pulse">
          <div className="mb-2 w-20 h-4 bg-gray-300 rounded"></div>
          <div className="flex flex-wrap gap-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex gap-1 items-center">
                <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                <div className="w-16 h-3 bg-gray-300 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-64 text-red-600">
        <div className="mb-2 text-lg">Error loading events</div>
        <div className="mb-4 text-sm">{error}</div>
        <Button onClick={() => loadEvents()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="h-full calendar-view">
      {/* Filter Section */}
      {showFilters && (
        <div className="p-4 mb-4 bg-gray-50 rounded-lg border">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="block mb-2 text-sm font-medium">
                Filter by User
              </label>
              <Combobox
                options={[
                  { value: "all", label: "All users" },
                  ...users.map((user) => ({
                    value: user.uid.toString(),
                    label: `${user.ufirstn} ${user.ulastn}`,
                  })),
                ]}
                placeholder="Search users..."
                emptyMessage="No users found."
                value={selectedUser}
                onChange={setSelectedUser}
                width="100%"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium">
                Filter by Event Type
              </label>
              <Select
                value={selectedEventType}
                onValueChange={setSelectedEventType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All event types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All event types</SelectItem>
                  <SelectItem value="Inspection">Inspection</SelectItem>
                  <SelectItem value="Service Call">Service Call</SelectItem>
                  <SelectItem value="Client Appointment">
                    Client Appointment
                  </SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                  <SelectItem value="Personal Time">Personal Time</SelectItem>
                  <SelectItem value="Adjustment">Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView={currentView}
        initialDate={currentDate}
        timeZone="local"
        headerToolbar={{
          left: "myCustomButton2",
          center: "prev title next",
          right:
            "myCustomButton today dayGridMonth,timeGridWeek,timeGridDay,listWeek",
        }}
        events={events}
        eventClick={handleEventClick}
        eventChange={handleEventChange}
        eventContent={eventContent}
        datesSet={handleDatesSet}
        customButtons={{
          myCustomButton: {
            text: "Filters",
            click: function () {
              setShowFilters(!showFilters);
            },
          },
          myCustomButton2: {
            text: "Add Event",
            click: function () {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any).$("#modal-container").modal("show");
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any).goM("raes-eventsAddEdit", "add1", null, null);
            },
          },
        }}
        height="auto"
        aspectRatio={1.35}
        eventDisplay="block"
        dayMaxEvents={3}
        moreLinkClick="popover"
        weekends={true}
        navLinks={true}
        editable={true}
        selectable={true}
        selectMirror={true}
        businessHours={{
          daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
          startTime: "08:00",
          endTime: "17:00",
        }}
        allDaySlot={true}
        nowIndicator={true}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short",
        }}
        titleFormat={{ month: "long", year: "numeric" }}
      />

      {/* Event Type Legend */}
      <div className="p-4 mt-4 bg-gray-50 rounded-lg">
        <h3 className="mb-2 text-sm font-semibold">Event Types</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(EVENT_TYPE_COLORS).map(
            ([type, color]) =>
              type !== "default" && (
                <div key={type} className="flex gap-1 items-center">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs capitalize">{type}</span>
                </div>
              )
          )}
        </div>
      </div>
    </div>
  );
}

CalendarView.displayName = "CalendarView";

setCustomElementMetadata({
  component: CalendarView,
  tagName: "raes-calendar-view",
  observedAttributes: {
    "api-url": (value: string) => ({ value, prop: "apiUrl" }),
    "api-stage": (value: string) => ({ value, prop: "apiStage" }),
  },
});
